import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import api from '../api';

const fmtCurrency = (v) => v != null ? `$${Number(v).toFixed(2)}` : '$0.00';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getSalesOrder(id);
        setOrder(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handlePrint = () => window.print();

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!order) return <div className="p-8 text-center text-red-500">Order not found</div>;

  return (
    <div data-testid="invoice-print-page">
      {/* Print Controls - hidden when printing */}
      <div className="print:hidden bg-card border-b px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-muted">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-sm text-muted-foreground flex-1">Invoice Preview — {order.order_number}</span>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
          data-testid="print-invoice-btn">
          <Printer className="h-4 w-4" /> Print Invoice
        </button>
      </div>

      {/* Invoice Content */}
      <div ref={printRef} className="max-w-[800px] mx-auto bg-white p-8 my-6 print:my-0 print:p-6 print:shadow-none shadow-lg" style={{color: '#111'}}>
        {/* Company Header */}
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex items-start gap-3">
            <img src="/logo.jpg" alt="Webb Supply" className="h-14 w-14 rounded-md object-cover" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{color: '#111'}}>Webb Supply</h1>
              <div className="text-sm mt-1" style={{color: '#444'}}>
                <div>227 County Road 125 East</div>
                <div>Norris City, IL 62869</div>
                <div>618-962-3211</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold tracking-tight" style={{color: '#111'}}>{(order.status || '').toLowerCase() === 'quote' ? 'SALES QUOTE' : 'INVOICE'}</h2>
            <div className="text-sm mt-2" style={{color: '#444'}}>
              <div><strong>Invoice #:</strong> {order.order_number}</div>
              <div><strong>Date:</strong> {fmtDate(order.order_date || order.created_at)}</div>
              {order.due_date && <div><strong>Due Date:</strong> {order.due_date}</div>}
              {order.payment_terms && <div><strong>Terms:</strong> {order.payment_terms}</div>}
              {(order.picked_up_by || order.contact_name) && <div><strong>Picked Up By:</strong> {order.picked_up_by || order.contact_name}</div>}
            </div>
          </div>
        </div>

        {/* Bill To / Ship To — Bill To positioned for #10 envelope window */}
        <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
          <div style={{marginTop: '0.25in', minHeight: '1in'}}>
            <div className="font-semibold text-xs uppercase tracking-wider mb-2" style={{color: '#666'}}>Bill To</div>
            <div className="font-semibold text-base" style={{color: '#111'}}>{order.customer_name || '—'}</div>
            {order.billing_address1 && <div>{order.billing_address1}</div>}
            {order.billing_address2 && <div>{order.billing_address2}</div>}
            {(order.billing_city || order.billing_state || order.billing_postal_code) && (
              <div>{[order.billing_city, order.billing_state, order.billing_postal_code].filter(Boolean).join(', ')}</div>
            )}
            {order.billing_country && <div>{order.billing_country}</div>}
            {(order.customer_phone || order.phone) && <div style={{color: '#444'}}>{order.customer_phone || order.phone}</div>}
            {(order.customer_email || order.email) && <div style={{color: '#444'}}>{order.customer_email || order.email}</div>}
          </div>
          <div>
            <div className="font-semibold text-xs uppercase tracking-wider mb-2" style={{color: '#666'}}>Ship To</div>
            {order.ship_to_company && <div className="font-semibold">{order.ship_to_company}</div>}
            {order.shipping_address1 && <div>{order.shipping_address1}</div>}
            {order.shipping_address2 && <div>{order.shipping_address2}</div>}
            {(order.shipping_city || order.shipping_state || order.shipping_postal_code) && (
              <div>{[order.shipping_city, order.shipping_state, order.shipping_postal_code].filter(Boolean).join(', ')}</div>
            )}
          </div>
        </div>

        {/* Order Info Row */}
        <div className="grid grid-cols-4 gap-4 mb-6 text-xs bg-gray-100 rounded px-4 py-2" style={{color: '#444'}}>
          {order.lease_name && <div><strong>Lease:</strong> {order.lease_name}</div>}
          {order.sales_rep && <div><strong>Sales Rep:</strong> {order.sales_rep}</div>}
          {order.po_number && <div><strong>PO #:</strong> {order.po_number}</div>}
          {order.pricing_scheme && <div><strong>Pricing:</strong> {order.pricing_scheme}</div>}
        </div>

        {/* Line Items Table */}
        <table className="w-full mb-6 text-sm" style={{borderCollapse: 'collapse'}}>
          <thead>
            <tr style={{borderBottom: '2px solid #333'}}>
              <th className="text-left py-2 font-semibold" style={{color: '#111'}}>Item</th>
              <th className="text-left py-2 font-semibold" style={{color: '#111'}}>SKU</th>
              <th className="text-center py-2 font-semibold" style={{color: '#111'}}>Qty</th>
              <th className="text-right py-2 font-semibold" style={{color: '#111'}}>Unit Price</th>
              <th className="text-center py-2 font-semibold" style={{color: '#111'}}>Tax</th>
              <th className="text-right py-2 font-semibold" style={{color: '#111'}}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {(order.line_items || []).map((item, i) => (
              <tr key={i} style={{borderBottom: '1px solid #ddd'}}>
                <td className="py-2" style={{color: '#222'}}>{item.variant_name || item.description || '—'}</td>
                <td className="py-2 text-xs" style={{color: '#666'}}>{item.sku || ''}</td>
                <td className="py-2 text-center">{item.quantity}</td>
                <td className="py-2 text-right">{fmtCurrency(item.unit_price)}</td>
                <td className="py-2 text-center text-xs" style={{color: '#666'}}>{item.taxable !== false ? 'T' : ''}</td>
                <td className="py-2 text-right font-medium">{fmtCurrency(
                  // Defensive: line-items may store the line total under different keys depending on
                  // the ingestion path (cart=`total`, imported invoices=`subtotal`). Compute on the
                  // fly if neither is present.
                  item.total
                    ?? item.subtotal
                    ?? ((Number(item.quantity) || 0) * (Number(item.unit_price) || 0) - (Number(item.discount) || 0))
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 text-sm">
            <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
              <span style={{color: '#666'}}>Subtotal</span>
              <span className="font-medium">{fmtCurrency(order.subtotal)}</span>
            </div>
            {(order.tax > 0 || order.tax1_rate) && (
              <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
                <span style={{color: '#666'}}>Tax ({order.tax1_rate || '7.25'}%)</span>
                <span className="font-medium">{fmtCurrency(order.tax)}</span>
              </div>
            )}
            {order.freight > 0 && (
              <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
                <span style={{color: '#666'}}>Freight</span>
                <span className="font-medium">{fmtCurrency(order.freight)}</span>
              </div>
            )}
            {order.square_fee > 0 && (
              <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
                <span style={{color: '#666'}}>Card Fee</span>
                <span className="font-medium">{fmtCurrency(order.square_fee)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 font-bold text-base" style={{borderTop: '2px solid #333'}}>
              <span>Total</span>
              <span>{fmtCurrency(order.total)}</span>
            </div>
            {order.amount_paid > 0 && (
              <>
                <div className="flex justify-between py-1 text-green-700">
                  <span>Paid</span>
                  <span>{fmtCurrency(order.amount_paid)}</span>
                </div>
                <div className="flex justify-between py-1 font-bold">
                  <span>Balance Due</span>
                  <span>{fmtCurrency((order.total || 0) - (order.amount_paid || 0))}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Remarks */}
        {order.remarks && (
          <div className="mt-6 pt-4 border-t text-sm" style={{color: '#666'}}>
            <strong>Remarks:</strong> {order.remarks}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t text-center text-xs" style={{color: '#999'}}>
          Thank you for your business! — Webb Supply
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:my-0 { margin-top: 0 !important; margin-bottom: 0 !important; }
          .print\\:p-6 { padding: 1.5rem !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
